import React, { useState } from "react";
import {
  Drawer,
  List,
  Avatar,
  Select,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Empty,
  Collapse
} from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { saveWithConflictCheck } from "./shiftApi";
import { useIsMobile } from "../../../design-system/useMediaQuery";

const { Text } = Typography;
const STATUS_COLOR = { Pending: "gold", Approved: "green", Rejected: "red" };

const name = emp => (emp ? `${emp.firstName || ""} ${emp.lastName || ""}`.trim() : "");

/**
 * Pending swap requests from employees. `suggestionsFor(request)` returns
 * [{ employee, hours }] ranked for the requested day.
 */
const SwapRequestsDrawer = ({ open, onClose, requests, employees, suggestionsFor, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const setDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const respond = async (request, status) => {
    setBusyId(request._id);
    try {
      const draft = drafts[request._id] || {};
      const res = await saveWithConflictCheck(
        "put",
        `/swap-requests/${request._id}`,
        {
          status,
          replacement: status === "Approved" ? draft.replacement : undefined,
          responseNote: draft.note
        },
        t
      );
      if (res) {
        Success(res.data.successMessage);
        onChanged && onChanged();
      }
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setBusyId(null);
    }
  };

  const describe = request => {
    const day = moment(request.date, "YYYYMMDD").format("ddd DD/MMM/YYYY");
    return (
      <>
        <Text strong>{name(request.requester)}</Text> {t("wants to hand over")}{" "}
        <Tag color={request.shift?.color}>{request.shift?.title || t("deleted shift")}</Tag>
        {t("on")} <Text strong>{day}</Text>
      </>
    );
  };

  const pending = requests.filter(r => r.status === "Pending");
  const done = requests.filter(r => r.status !== "Pending");

  const renderPending = request => {
    const suggestions = suggestionsFor(request);
    const suggestedIds = new Set(suggestions.map(s => s.employee._id));
    const others = employees.filter(
      e => !suggestedIds.has(e._id) && e._id !== request.requester?._id
    );
    return (
      <List.Item key={request._id}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ overflowWrap: "anywhere" }}>{describe(request)}</div>
          {request.reason && <Text type="secondary">“{request.reason}”</Text>}
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("Replacement (optional)")}
            style={{ width: "100%" }}
            value={drafts[request._id]?.replacement}
            onChange={value => setDraft(request._id, { replacement: value })}
            options={[
              {
                label: t("Suggested (free, fewest hours this week)"),
                options: suggestions.map(s => ({
                  value: s.employee._id,
                  label: `${name(s.employee)} · ${s.hours.toFixed(1)}h`
                }))
              },
              {
                label: t("Everyone else"),
                options: others.map(e => ({ value: e._id, label: name(e) }))
              }
            ]}
          />
          <Input
            placeholder={t("Note to the employee (optional)")}
            value={drafts[request._id]?.note}
            onChange={e => setDraft(request._id, { note: e.target.value })}
          />
          <Space wrap>
            <Button
              type="primary"
              loading={busyId === request._id}
              onClick={() => respond(request, "Approved")}
            >
              {t("Approve")}
            </Button>
            <Button
              danger
              disabled={busyId === request._id}
              onClick={() => respond(request, "Rejected")}
            >
              {t("Reject")}
            </Button>
          </Space>
        </Space>
      </List.Item>
    );
  };

  return (
    <Drawer
      title={t("Shift swap requests")}
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : "min(560px, 100vw)"}
    >
      {pending.length === 0 ? (
        <Empty description={t("No open requests")} />
      ) : (
        <List dataSource={pending} renderItem={renderPending} />
      )}
      {done.length > 0 && (
        <Collapse
          ghost
          style={{ marginTop: 16 }}
          items={[
            {
              key: "done",
              label: `${t("Handled")} (${done.length})`,
              children: (
                <List
                  size="small"
                  dataSource={done}
                  renderItem={request => (
                    <List.Item key={request._id}>
                      <Space direction="vertical" size={2}>
                        <div>{describe(request)}</div>
                        <Space size={4} wrap>
                          <Tag color={STATUS_COLOR[request.status]}>{t(request.status)}</Tag>
                          {request.replacement && (
                            <Text type="secondary">
                              → <Avatar size={16} src={request.replacement.picture?.url || null} />{" "}
                              {name(request.replacement)}
                            </Text>
                          )}
                        </Space>
                      </Space>
                    </List.Item>
                  )}
                />
              )
            }
          ]}
        />
      )}
    </Drawer>
  );
};

export default SwapRequestsDrawer;
